import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * Width clamps for the desktop sidebar (in CSS px). The default mirrors the
 * legacy `16rem` value so sessions that pre-date the resize support keep
 * their existing layout. The maximum is wide enough to surface long route
 * labels without making the sidebar dominate the viewport on smaller
 * laptops; the minimum keeps the icon column + label column readable
 * without bumping into the rail.
 */
export const SIDEBAR_DEFAULT_WIDTH_PX = 256;
export const SIDEBAR_MIN_WIDTH_PX = 192;
export const SIDEBAR_MAX_WIDTH_PX = 480;
/**
 * If a drag would shrink the sidebar narrower than this, treat the gesture as
 * "collapse to icons" — the sidebar snaps to its icon-mode width and the
 * `open` flag flips to false. Reverse-mapped on expansion: a drag that grows
 * the rail past this point switches the sidebar back to expanded mode.
 */
export const SIDEBAR_COLLAPSE_THRESHOLD_PX = 140;

function clampWidth(value: number): number {
  if (!Number.isFinite(value)) {
    return SIDEBAR_DEFAULT_WIDTH_PX;
  }
  return Math.min(SIDEBAR_MAX_WIDTH_PX, Math.max(SIDEBAR_MIN_WIDTH_PX, Math.round(value)));
}

export const useSidebarStore = defineStore('sidebarStore', () => {
  const open = ref<boolean>(true);
  const width = ref<number>(SIDEBAR_DEFAULT_WIDTH_PX);

  const setOpen = (value: boolean) => {
    open.value = value;
  };

  const toggle = () => {
    open.value = !open.value;
  };

  const setWidth = (value: number) => {
    width.value = clampWidth(value);
  };

  return {
    open,
    width,
    setOpen,
    toggle,
    setWidth,
  };
}, {
  persist: {
    storage: localStorage,
    key: 'sidebarStore',
    pick: ['open', 'width'],
  },
});
